import React, { useState } from 'react';
import { useAdminData } from '../hooks/useAdminData';
import { formatCredits } from '../utils';
import { Input, Select, FormAlert, Text } from '../../components';
import { Button, Icon } from '@ui-library';
import { IconName } from '@ui-library/Icon/Icon.type';
import type { CreditType } from '../types';

interface AddCreditsModalProps {
  isOpen: boolean;
  userIdentifier: string;
  userDisplayName?: string;
  currentBalance?: number;
  onClose: () => void;
  onSuccess: () => void;
}

const CREDIT_TYPE_OPTIONS = [
  { label: 'Free Platform Credits', value: 'free_platform' },
  { label: 'Monthly Credits', value: 'monthly' },
  { label: 'Paid Platform Credits', value: 'paid_platform' },
];

const INITIAL_FORM = {
  amount: '',
  creditType: 'free_platform' as CreditType,
  description: '',
};

export const AddCreditsModal: React.FC<AddCreditsModalProps> = ({
  isOpen,
  userIdentifier,
  userDisplayName,
  currentBalance,
  onClose,
  onSuccess,
}) => {
  const { operations } = useAdminData();
  const [form, setForm] = useState(INITIAL_FORM);
  const [isClosing, setIsClosing] = useState(false);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      operations.clearError();
      setForm(INITIAL_FORM);
      onClose();
    }, 300);
  };

  const handleSubmit = async () => {
    const amount = parseFloat(form.amount);
    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid credit amount');
      return;
    }

    const success = await operations.addCredits(
      userIdentifier,
      amount,
      form.creditType,
      form.description || undefined
    );

    if (success) {
      setForm(INITIAL_FORM);
      onSuccess();
      handleClose();
    }
  };

  const title = userDisplayName ? `Add Credits to ${userDisplayName}` : 'Add Credits';

  if (!isOpen && !isClosing) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-alpha-black-70 z-[200] ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
        onClick={handleClose}
      />
      {/* Panel */}
      <div
        className={`fixed right-0 top-0 h-screen z-[200] bg-bg-primary w-[420px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        style={{
          boxShadow: '0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)',
        }}
      >
        {/* Header */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <Text variant="text-xl" weight="bold">{title}</Text>
          <button
            onClick={handleClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          <Input
            label="Credit Amount ($)"
            type="number"
            step="0.01"
            min="0.01"
            max="10000"
            value={form.amount}
            onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
            placeholder="0.00"
            required
            autoFocus
          />

          <Select
            label="Credit Type"
            options={CREDIT_TYPE_OPTIONS}
            value={form.creditType}
            onChange={(val) => setForm(prev => ({ ...prev, creditType: val as CreditType }))}
          />

          <div className="flex flex-col gap-bg-v2-xxs">
            <label className="font-bg-v2-sans font-bg-v2-medium text-bg-v2-sm text-text-secondary">
              Description (Optional)
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              className="w-full px-bg-v2-lg py-bg-v2-md border-2 border-border-secondary rounded-bg-v2-md focus:outline-none focus:border-border-primary-hover text-bg-v2-md text-text-primary bg-bg-primary placeholder:text-text-placeholder"
              rows={3}
              placeholder="Reason for credit grant..."
            />
          </div>

          {currentBalance !== undefined && (
            <div className="bg-bg-brand-primary p-bg-v2-lg rounded-bg-v2-md flex flex-col gap-bg-v2-xxs">
              <Text variant="text-sm" weight="medium" color="brand-secondary" as="p">
                Current Balance: {formatCredits(currentBalance)}
              </Text>
              <Text variant="text-sm" color="brand-primary" as="p">
                User will receive the credits immediately after confirmation.
              </Text>
            </div>
          )}

          {operations.error && (
            <FormAlert variant="error" message={operations.error} />
          )}
        </div>

        {/* Footer */}
        <div className="flex p-bg-v2-xl px-bg-v2-3xl justify-end items-center gap-bg-v2-xl self-stretch border-t border-border-secondary bg-bg-primary">
          <Button
            variant="outline"
            label="Cancel"
            onClick={handleClose}
            disabled={operations.loading}
          />
          <Button
            variant="success"
            label={operations.loading ? 'Adding...' : 'Add Credits'}
            onClick={handleSubmit}
            disabled={operations.loading}
          />
        </div>
      </div>
    </>
  );
};

export default AddCreditsModal;
